import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, Loader2, Shield, Calendar, User, Building, Award, FileText } from 'lucide-react';

// People Logo
const PeopleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="6" r="3.5" />
    <path d="M6 17.5c0-3.5 2.7-6 6-6s6 2.5 6 6v1.5H6v-1.5z" />
    <circle cx="4" cy="8" r="2" opacity="0.6" />
    <path d="M0 15c0-2.2 1.8-4 4-4s4 1.8 4 4v1H0v-1z" opacity="0.6" />
    <circle cx="20" cy="8" r="2" opacity="0.6" />
    <path d="M16 15c0-2.2 1.8-4 4-4s4 1.8 4 4v1h-8v-1z" opacity="0.6" />
  </svg>
);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyDocument() {
  const { docType, verificationCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    verifyDocument();
  }, [docType, verificationCode]);

  const verifyDocument = async () => {
    try {
      const response = await fetch(`${API_URL}/api/verify/${docType}/${verificationCode}`);
      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError('Erreur lors de la vérification');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (statut) => {
    switch (statut) {
      case 'Délivrée':
      case 'Valide':
        return 'bg-green-100 text-green-800';
      case 'Expirée':
        return 'bg-red-100 text-red-800';
      case 'En cours':
      case 'À valider':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-blue-500 mb-4" />
            <p className="text-gray-600 text-lg">Vérification en cours...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center border-b">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-xl bg-blue-600 flex items-center justify-center">
              <PeopleIcon className="w-10 h-10 text-white" />
            </div>
          </div>
          <CardTitle className="text-xl">Vérification de Document</CardTitle>
          <CardDescription>People Management</CardDescription>
        </CardHeader>
        
        <CardContent className="pt-6">
          {error ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-red-600 mb-2">Erreur de vérification</h3>
              <p className="text-gray-600">{error}</p>
            </div>
          ) : result && !result.valid ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-red-600 mb-2">Document non valide</h3>
              <p className="text-gray-600">{result.message}</p>
              <p className="text-sm text-gray-500 mt-4">
                Le code de vérification ne correspond à aucun document dans notre système.
              </p>
            </div>
          ) : result && result.valid ? (
            <div className="space-y-6">
              {/* Verification Success Header */}
              <div className="text-center pb-4 border-b">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-green-600 mb-1">Document authentique</h3>
                <p className="text-gray-600">Ce document a été vérifié avec succès</p>
              </div>

              {/* Document Type & Status */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {result.type === 'Autorisation de Conduite' ? (
                    <Shield className="h-5 w-5 text-blue-600" />
                  ) : (
                    <Award className="h-5 w-5 text-purple-600" />
                  )}
                  <span className="font-medium">{result.type}</span>
                </div>
                {result.statut && (
                  <Badge className={getStatusColor(result.statut)}>
                    {result.statut}
                  </Badge>
                )}
              </div>

              {/* Document Details */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                {result.numero && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Numéro
                    </span>
                    <span className="font-mono font-medium">{result.numero}</span>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-gray-600 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Titulaire
                  </span>
                  <span className="font-medium">{result.collaborateur}</span>
                </div>

                {result.matricule && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Matricule</span>
                    <span className="font-mono">{result.matricule}</span>
                  </div>
                )}

                {result.categories_caces && result.categories_caces.length > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Catégories CACES</span>
                    <div className="flex gap-1 flex-wrap justify-end">
                      {result.categories_caces.map((cat, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {cat}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {result.formation && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Formation</span>
                    <span className="font-medium text-right">{result.formation}</span>
                  </div>
                )}

                {result.organisme && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Organisme</span>
                    <span>{result.organisme}</span>
                  </div>
                )}

                <div className="border-t pt-3 mt-3">
                  {(result.date_debut || result.date_formation) && (
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-600 flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {result.date_debut ? 'Date début' : 'Date formation'}
                      </span>
                      <span>{formatDate(result.date_debut || result.date_formation)}</span>
                    </div>
                  )}

                  {(result.date_fin || result.date_fin_validite) && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Date fin validité</span>
                      <span>{formatDate(result.date_fin || result.date_fin_validite)}</span>
                    </div>
                  )}
                </div>

                <div className="border-t pt-3 mt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      Entreprise
                    </span>
                    <span className="font-medium">{result.entreprise}</span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center text-xs text-gray-500 pt-4 border-t">
                <p>Document vérifié le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR')}</p>
                <p className="mt-1">Code de vérification : <span className="font-mono">{verificationCode}</span></p>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
